import { SlideHeader } from "../../../SlideHeader";

const PIPELINE: Array<{ step: string; detail: string }> = [
  {
    step: "Ticket",
    detail: "Written by the founders in Linear; selected and readiness-checked by the scheduler",
  },
  {
    step: "Agent team",
    detail: "An architect plans; specialist builder agents implement; an adversarial QA agent must approve — its default verdict is REJECTED",
  },
  {
    step: "Verification",
    detail: "Lint, typecheck, tests, and build across all 7 apps of the platform — green is the only gate to release",
  },
  {
    step: "Staging",
    detail: "Auto-deploy, Playwright UI suites, and an 8-app audit-engine invariants gate",
  },
  {
    step: "Production",
    detail: "Shipped nightly by an unattended deploy session — 23 automated gates end to end",
  },
];

const SESSIONS: Array<{ name: string; cadence: string; detail: string }> = [
  {
    name: "Build",
    cadence: "every 4 hours",
    detail:
      "Selects ready tickets, implements them with agent teams, verifies, and lands on the release branch — staging deploys automatically.",
  },
  {
    name: "Detect & repair",
    cadence: "nightly",
    detail:
      "Reads production telemetry — error logs, delivery tripwires, cost-burst monitors — triages, files tickets with evidence, then fixes the serious ones itself the same night.",
  },
  {
    name: "Ship",
    cadence: "nightly",
    detail:
      "Re-verifies the release from scratch, confirms staging passed its test suites, then triggers and monitors the production deploy. No human in the loop.",
  },
];

const STATS: Array<{ value: string; label: string }> = [
  { value: "100%", label: "of code AI-authored — inside a factory the founder engineered and oversees" },
  { value: "599", label: "PRs merged in the last 90 days" },
  { value: "nightly", label: "autonomous production deploys" },
  { value: "23", label: "automated gates from code to prod" },
  { value: "3–6", label: "agents on each ticket team" },
  { value: "~1M", label: "lines of TypeScript maintained" },
];

export default function AiNativeFactory() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-5">
      <SlideHeader unnumbered eyebrow="Appendix F · The AI-Native Factory">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          An engineering org{" "}
          <span className="text-blue-700">made of AI agents</span> — run by one
          founder.
        </h1>
        <p className="mt-3 text-lg font-medium italic tracking-tight text-slate-700 md:text-xl">
          Agent teams plan, build, and adversarially test every ticket;
          scheduled sessions repair production and ship nightly — unattended.
          The factory itself is the founder's engineering.
        </p>
      </SlideHeader>

      {/* The pipeline — ticket to production */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
          Every ticket travels the same pipeline
        </p>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-5 md:gap-3">
          {PIPELINE.map((p, i) => (
            <div
              key={p.step}
              className="rounded-lg bg-slate-50 p-2.5 ring-1 ring-slate-200 md:p-3"
            >
              <p className="text-sm font-bold tracking-tight text-slate-900 md:text-base">
                <span className="text-slate-400">{i + 1} · </span>
                {p.step}
              </p>
              <p className="mt-1 text-xs leading-snug text-slate-600">
                {p.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* The three scheduled sessions */}
      <div className="mt-4 md:mt-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-sm">
          Three unattended sessions run the org
        </p>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-3">
          {SESSIONS.map((s) => (
            <div
              key={s.name}
              className="rounded-lg bg-blue-50/50 p-3 ring-1 ring-blue-200 md:p-4"
            >
              <p className="text-sm font-bold tracking-tight text-slate-900 md:text-base">
                {s.name}
                <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-blue-700">
                  {s.cadence}
                </span>
              </p>
              <p className="mt-1 text-xs leading-snug text-slate-700 md:text-sm">
                {s.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Numbers */}
      <div className="mt-4 grid grid-cols-3 gap-2 md:mt-5 md:grid-cols-6 md:gap-3">
        {STATS.map((s) => (
          <div
            key={s.label}
            className="rounded-lg bg-slate-50 p-2.5 ring-1 ring-slate-200 md:p-3"
          >
            <p className="text-lg font-bold tracking-tight text-slate-900 md:text-xl">
              {s.value}
            </p>
            <p className="mt-0.5 text-xs leading-snug text-slate-600">
              {s.label}
            </p>
          </div>
        ))}
      </div>

      {/* The thesis, operating */}
      <div className="mt-4 rounded-xl bg-blue-50 px-5 py-3.5 ring-1 ring-blue-200 md:mt-auto md:px-6 md:py-4">
        <p className="text-sm leading-snug text-slate-800 md:text-base">
          <span className="font-semibold text-blue-900">
            “Coding is largely solved.”
          </span>{" "}
          — Boris Cherny, creator of Claude Code (Feb 2026).{" "}
          <span className="text-slate-700">
            Apptonomy is that thesis operating as a company — the build-cost
            counterfactual is Appendix D ($22–25M, 26 FTE, pre-AI); the spend
            consequence is slide 17’s 9%.
          </span>
        </p>
      </div>
    </div>
  );
}
